import type { OrgCustomColors } from '@cognitoforms/types/server-types/model/org-custom-colors';
import iro from '@jaames/iro';
import ColorContrastChecker from 'color-contrast-checker';
import { darkenHex, lightenHex, mixHex } from '@cognitoforms/utils/color-utils';
import type { OrgAppearanceSettingsMeta } from '@cognitoforms/types/server-types/model/org-appearance-settings-meta';
import { ColorScheme } from '@cognitoforms/types/server-types/model/color-scheme';

// Default helpers functions
export function getDefaults() {
	return {
		Text: '#234652',
		Background: '#ffffff',
		Primary: '#09a098',
		Secondary: '#94cc3f',
		Important: '#d85427',
		// --green-5
		successDark: '#bfedb6',
		successLight: '#94cc3f',
		// --red-4
		errorDark: '#ffabb0',
		errorLight: '#c5151d',
		// --yellow-3
		warningDark: '#ffcc00',
		warningLight: '#de7a00'
	};
}

export function isDefaultColors(colors: OrgCustomColors) {
	if (!colors)
		return true;
	const defaultColors = getDefaults();
	// renmove spaces for comparison
	return colors.Text.replace(/\s+/g, '') === defaultColors.Text
		&& colors.Background.replace(/\s+/g, '') === defaultColors.Background
		&& colors.Primary.replace(/\s+/g, '') === defaultColors.Primary
		&& colors.Secondary.replace(/\s+/g, '') === defaultColors.Secondary
		&& colors.Important.replace(/\s+/g, '') === defaultColors.Important;
}

// Contrast compatibility helper functions
export function isAACompliant(color1: string, color2: string) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelAA(color1, color2);
}

export function isCustomCompliant(color1: string, color2: string, ratio: number) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelCustom(color1, color2, ratio);
}

// Updating Custom Theme functions
/** Compare the relative darkness of two colors
 * @param color1 string in hex
 * @param color2 string in hex
 * @returns false if color1 is darker than color2, true otherwise
 */
function calculateRelativeDarkness(color1: string, color2: string) {
	const iroColor1 = new iro.Color(color1);
	const iroColor2 = new iro.Color(color2);

	return iroColor1.hsv.v > iroColor2.hsv.v;
}

function calculateIsColorDark(color: string) {
	const iroColor = new iro.Color(color);
	return iroColor.hsv.v <= 50;
}

function calculateDerivedColor(color: string, derivedColor: string, isDark: boolean, hasReversed: boolean = false): [string, boolean, boolean] {
	derivedColor = isDark ? lightenHex(derivedColor) : darkenHex(derivedColor);

	// If we hit black or white and it's still not compliant, reverse direction
	if (derivedColor === '#000000' || derivedColor === '#ffffff') {
		// Sanity check to prevent infinite loop, if somehow it is not compliant with either white or black,
		// return either white or black based on which direction it originally went
		if (hasReversed) // returning the opposite of isDark since we know it flipped
			return isDark ? ['#000000', isDark, hasReversed] : ['#ffffff', isDark, hasReversed];
		else {
			isDark = !isDark;
			derivedColor = color;
			hasReversed = true;
		}
	}

	return [derivedColor, isDark, hasReversed];
}

function getCompliantColor(color: string, baseContrastColor: string | null = null, customRatio: number | null = null) {
	let derivedColor = color;
	let isDark = baseContrastColor ? calculateRelativeDarkness(baseContrastColor, color) : calculateIsColorDark(color);

	// if the color is 'dark', and the base contrast color was not specified, use white as the text color
	if (isDark && !baseContrastColor)
		return '#ffffff';

	let hasReversed = false;

	if (customRatio) // If custom ratio is specified, we will derive the color until it is compliant with the custom ratio (ex. primary icons on bg)
		while (!isCustomCompliant(derivedColor, baseContrastColor ?? color, customRatio))
			[derivedColor, isDark, hasReversed] = calculateDerivedColor(color, derivedColor, isDark, hasReversed);
	else // else, derive the color until it is AA compliant
		while (!isAACompliant(derivedColor, baseContrastColor ?? color))
			[derivedColor, isDark, hasReversed] = calculateDerivedColor(color, derivedColor, isDark, hasReversed);

	return derivedColor;
}

export function calculateIsDark (hasCustomTheme: boolean, useSystemColors: boolean, colors: OrgCustomColors, userColorScheme: ColorScheme) {
	// If the org has color customization, rely on that first
	if (hasCustomTheme && colors)
		return calculateRelativeDarkness(colors.Text, colors.Background);
	// If the user has a color scheme set, use that
	else if (useSystemColors) {
		if (userColorScheme === ColorScheme.System)
			return window.matchMedia('(prefers-color-scheme: dark)').matches;

		return userColorScheme === ColorScheme.Dark;
	}
	// If the org has no custom theme and user has no color scheme, return false for dark
	return false;
}

export function getDerivedColors(colors: OrgCustomColors) {
	if (!colors || isDefaultColors(colors)) {
		return {
			derivedTextForPrimary: 'initial',
			derivedTextForImportant: 'initial',
			derivedPrimaryTextForBackground: 'initial',
			derivedPrimaryIconForBackground: 'initial'
		};
	}

	// Calculate custom compliant colors, these calculate what text to use on a specific background color (mostly button text)
	// For text color on an important/primary background, we do not want to derive the text color if it is the default color
	const derivedTextForPrimary = colors.Primary === getDefaults().Primary ? 'initial' : getCompliantColor(colors.Primary); // the text color we use on a primary background
	const derivedTextForImportant = colors.Important === getDefaults().Important ? 'initial' : getCompliantColor(colors.Important); // the text color we use on an important background

	// the colors below are sometimes used as text on a background that is a mix of the background and text colors, so we need to make sure it is compliant with the text color on the background
	const mixTextAndBackground = mixHex(colors.Background, colors.Text, .93);
	const derivedPrimaryTextForBackground = getCompliantColor(colors.Primary, mixTextAndBackground); // the text color we use on background, derived from the primary color
	const derivedPrimaryIconForBackground = getCompliantColor(colors.Primary, mixTextAndBackground, (3 / 1)); // the icon color we use on background, derived from the primary color

	return {
		derivedTextForPrimary,
		derivedTextForImportant,
		derivedPrimaryTextForBackground,
		derivedPrimaryIconForBackground
	};
}

export function getCustomColorsFromCss() {
	const computedStyle = getComputedStyle(document.documentElement);
	const orgColors = {
		Text: computedStyle.getPropertyValue('--text').trim(),
		Background: computedStyle.getPropertyValue('--background').trim(),
		Primary: computedStyle.getPropertyValue('--primary').trim(),
		Secondary: computedStyle.getPropertyValue('--secondary').trim(),
		Important: computedStyle.getPropertyValue('--important').trim()
	} as OrgCustomColors;

	// Fallback to defaults if any color is not set
	if (Object.values(orgColors).some(color => color === ''))
		return getDefaults() as OrgCustomColors;

	return orgColors;
}

function getUserColorSchemeFromCss() {
	const colorScheme = getComputedStyle(document.documentElement).getPropertyValue('--user-color-scheme');
	if (colorScheme === 'dark')
		return ColorScheme.Dark;
	else if (colorScheme === 'light')
		return ColorScheme.Light;
	else if (colorScheme === 'system')
		return ColorScheme.System;
	return null;
}

export function updateCustomTheme(useSystemColors: boolean, colors: OrgCustomColors, userColorScheme: ColorScheme) {
	if (!colors)
		colors = getCustomColorsFromCss();

	const hasCustomTheme = !isDefaultColors(colors);
	if (hasCustomTheme) {
		document.documentElement.style.setProperty('--text', colors.Text);
		document.documentElement.style.setProperty('--background', colors.Background);
		document.documentElement.style.setProperty('--primary', colors.Primary);
		document.documentElement.style.setProperty('--secondary', colors.Secondary);
		document.documentElement.style.setProperty('--important', colors.Important);

		const derivedColors = getDerivedColors(colors);
		document.documentElement.style.setProperty('--derived-text-for-primary', derivedColors.derivedTextForPrimary);
		document.documentElement.style.setProperty('--derived-text-for-important', derivedColors.derivedTextForImportant);
		document.documentElement.style.setProperty('--derived-primary-text-for-background', derivedColors.derivedPrimaryTextForBackground);
		document.documentElement.style.setProperty('--derived-primary-icon-for-background', derivedColors.derivedPrimaryIconForBackground);
		document.documentElement.setAttribute('data-has-custom-theme', 'true');

		if (colors.Background !== getDefaults().Background) {
			const isDark = calculateRelativeDarkness(colors.Text, colors.Background);
			const defaults = getDefaults();

			// Check loose icon compliance with the background color
			const successIconCompliant = isAACompliant(isDark ? defaults.successDark : defaults.successLight, colors.Background);
			const errorIconCompliant = isAACompliant(isDark ? defaults.errorDark : defaults.errorLight, colors.Background);
			const warningIconCompliant = isAACompliant(isDark ? defaults.warningDark : defaults.warningLight, colors.Background);;

			document.documentElement.style.setProperty('--icon-success', successIconCompliant ? 'initial' : 'var(--shade-9)');
			document.documentElement.style.setProperty('--icon-warning', warningIconCompliant ? 'initial' : 'var(--shade-9)');
			document.documentElement.style.setProperty('--icon-error', errorIconCompliant ? 'initial' : 'var(--shade-9)');
		}
	}
	else {
		document.documentElement.style.setProperty('--text', 'initial');
		document.documentElement.style.setProperty('--background', 'initial');
		document.documentElement.style.setProperty('--primary', 'initial');
		document.documentElement.style.setProperty('--secondary', 'initial');
		document.documentElement.style.setProperty('--important', 'initial');
		document.documentElement.style.setProperty('--derived-text-for-primary', 'initial');
		document.documentElement.style.setProperty('--derived-text-for-important', 'initial');
		document.documentElement.style.setProperty('--derived-primary-text-for-background', 'initial');
		document.documentElement.style.setProperty('--derived-primary-icon-for-background', 'initial');
		document.documentElement.style.setProperty('--icon-success', 'initial');
		document.documentElement.style.setProperty('--icon-warning', 'initial');
		document.documentElement.style.setProperty('--icon-error', 'initial');

		document.documentElement.setAttribute('data-has-custom-theme', 'false');
	}

	// If colorScheme is not passed in, get it from the CSS
	if (!userColorScheme)
		userColorScheme = getUserColorSchemeFromCss();

	document.documentElement.setAttribute('data-color-scheme', calculateIsDark(hasCustomTheme, useSystemColors, colors, userColorScheme) ? 'dark' : 'light');
}

// Update Default Typeface
export function updateTypeface(useDefaultTypeface: boolean) {
	if (useDefaultTypeface === null) {
		// get the default value from the CSS
		useDefaultTypeface = getComputedStyle(document.documentElement).getPropertyValue('--font-display').trim() === 'system-ui';
	}
	else if (useDefaultTypeface)
		document.documentElement.style.setProperty('--font-display', 'system-ui');
	else
		document.documentElement.style.removeProperty('--font-display');
}

function removeFaviconLinks() {
	const iconLinks = document.querySelectorAll('link[rel~="icon"]');
	iconLinks.forEach(link => link.parentNode?.removeChild(link));
	const appleTouchLinks = document.querySelectorAll('link[rel="apple-touch-icon"]');
	appleTouchLinks.forEach(link => link.parentNode?.removeChild(link));
}

// Update favicon to the provided image URL
export function updateFavicon(logoUrl: string, faviconUrl: string) {
	const iconLinks = document.querySelectorAll('link[rel~="icon"]');
	const hasDefaultFavicons = iconLinks.length > 1;
	const hasCustomFavicon = iconLinks.length === 1;

	if (!faviconUrl) {
		if (hasCustomFavicon) {
			removeFaviconLinks();

			// Re-Insert default favicons (from favicons.html)
			const defaultFavicons = [
				{ rel: 'icon', type: 'image/x-icon', href: '/favicons/favicon.ico' },
				{ rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicons/favicon-16x16.png' },
				{ rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicons/favicon-32x32.png' },
				{ rel: 'icon', type: 'image/png', sizes: '48x48', href: '/favicons/favicon-48x48.png' }
			];
			const defaultAppleTouchIcons = [
				{ rel: 'apple-touch-icon', sizes: '57x57', href: '/favicons/apple-touch-icon-57x57.png' },
				{ rel: 'apple-touch-icon', sizes: '60x60', href: '/favicons/apple-touch-icon-60x60.png' },
				{ rel: 'apple-touch-icon', sizes: '72x72', href: '/favicons/apple-touch-icon-72x72.png' },
				{ rel: 'apple-touch-icon', sizes: '76x76', href: '/favicons/apple-touch-icon-76x76.png' },
				{ rel: 'apple-touch-icon', sizes: '114x114', href: '/favicons/apple-touch-icon-114x114.png' },
				{ rel: 'apple-touch-icon', sizes: '120x120', href: '/favicons/apple-touch-icon-120x120.png' },
				{ rel: 'apple-touch-icon', sizes: '144x144', href: '/favicons/apple-touch-icon-144x144.png' },
				{ rel: 'apple-touch-icon', sizes: '152x152', href: '/favicons/apple-touch-icon-152x152.png' },
				{ rel: 'apple-touch-icon', sizes: '167x167', href: '/favicons/apple-touch-icon-167x167.png' },
				{ rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/apple-touch-icon-180x180.png' },
				{ rel: 'apple-touch-icon', sizes: '1024x1024', href: '/favicons/apple-touch-icon-1024x1024.png' }
			];
			for (const icon of defaultFavicons) {
				const link = document.createElement('link');
				link.rel = icon.rel;
				link.type = icon.type;
				link.href = window.CognitoConfiguration?.SpaAssetsUrl + icon.href;
				if (icon.sizes) link.sizes = icon.sizes;
				document.head.appendChild(link);
			}
			for (const icon of defaultAppleTouchIcons) {
				const link = document.createElement('link');
				link.rel = icon.rel;
				link.href = window.CognitoConfiguration?.SpaAssetsUrl + icon.href;
				if (icon.sizes) link.sizes = icon.sizes;
				document.head.appendChild(link);
			}
		}
	}
	else {// use custom favicon
		const iconLink = iconLinks[0] as HTMLLinkElement;
		// Remove all existing favicon and apple-touch-icon links if new favicon
		if (hasDefaultFavicons || iconLink?.href !== faviconUrl) {
			removeFaviconLinks();

			// Create new favicon link for custom logo
			const newLink = document.createElement('link');
			newLink.rel = 'icon';
			newLink.type = faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
			newLink.href = faviconUrl;
			document.head.appendChild(newLink);

			// Also add apple-touch-icon for iOS home screen
			// Low Res
			const lowResAppleTouchLink = document.createElement('link');
			lowResAppleTouchLink.rel = 'apple-touch-icon';
			lowResAppleTouchLink.sizes = '60x60';
			lowResAppleTouchLink.href = newLink.href;
			document.head.appendChild(lowResAppleTouchLink);

			// High Res
			const highResAppleTouchLink = document.createElement('link');
			highResAppleTouchLink.rel = 'apple-touch-icon';
			highResAppleTouchLink.sizes = '180x180';
			highResAppleTouchLink.href = logoUrl;
			document.head.appendChild(highResAppleTouchLink);
		}
	}
}

// Update Org Appearance
export function updateOrgAppearance(useSystemColors: boolean, orgAppearanceSettings: OrgAppearanceSettingsMeta, userColorScheme: ColorScheme) {
	updateCustomTheme(useSystemColors, orgAppearanceSettings?.Colors, userColorScheme);
	updateTypeface(orgAppearanceSettings?.UseDefaultTypeface);
	updateFavicon(orgAppearanceSettings?.LogoLink, orgAppearanceSettings?.FaviconLink);
}